import {
  type AfterViewInit,
  Component,
  type ElementRef,
  EventEmitter,
  HostListener,
  input,
  type OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import {
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import type { Creditor } from '@core/models/creditor';
import { FinoButtonModule } from '@fino-ui/button';
import { ibanValidator, SubmittableForm } from '@fino-ui/forms';
import { FinoInputModule } from '@fino-ui/input';
import { FinoTranslationModule } from '@fino-ui/translation';
import { CreditorSearchComponent } from '../../../../components/direct-debits/creditor-search/creditor-search.component';

const IBAN_MASK = 'AA00 AAAA AAAA AAAA AAAA 9999 9999 9999';
export const EDITABLE_FIELD_NAME = 'editableField';

@Component({
  selector: 'app-cell-editor',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    FinoInputModule,
    FinoTranslationModule,
    FinoButtonModule,
    CreditorSearchComponent,
  ],
  templateUrl: './cell-editor.component.html',
  styleUrls: ['./cell-editor.component.sass'],
})
export class CellEditorComponent implements OnInit, AfterViewInit {
  readonly value = input<string>(undefined);
  readonly type = input<string>(undefined);
  readonly tableType = input(undefined);
  readonly tdElement = input<HTMLTableCellElement>(undefined);

  @ViewChild('cellEditor') boxRef!: ElementRef<HTMLElement>;

  ngAfterViewInit(): void {
    this.positionEditor();
  }

  private positionEditor(): void {
    const box = this.boxRef?.nativeElement;
    const td = this.tdElement();

    if (!box || !td) return;

    requestAnimationFrame(() => {
      const tdRect = td.getBoundingClientRect();
      const boxRect = box.getBoundingClientRect();
      const viewportWidth = window.innerWidth;

      let left = tdRect.left;

      if (left + boxRect.width > viewportWidth) {
        left = viewportWidth - boxRect.width - 10;
      }

      if (left < 0) {
        left = 10;
      }

      box.style.left = `${left}px`;
    });
  }

  @HostListener('window:scroll')
  @HostListener('window:resize')
  onScrollOrResize() {
    this.positionEditor();
  }

  @Output() cellEdited = new EventEmitter<{
    value?: string;
    dependantFields?: { key: string; value: string }[];
  }>();

  form = new SubmittableForm({
    [EDITABLE_FIELD_NAME]: new FormControl(''),
  });

  get isTextArea() {
    return 'purpose' === this.type();
  }

  get isCreditorIdInput() {
    return 'creditorId' === this.type();
  }

  get isCreditorNameInput() {
    return 'name' === this.type() && 'directDebits' === this.tableType();
  }

  handleCreditorResultSelected(creditor: Creditor) {
    this.cellEdited.next({
      dependantFields: [
        { key: 'name', value: creditor.name },
        { key: 'creditorId', value: creditor.creditorId },
      ],
    });
  }

  mask = null;

  getValidatorsByType(type: string) {
    const validators = [Validators.required];
    this.mask = null;

    if ('iban' === type) {
      validators.push(ibanValidator);
      this.mask = IBAN_MASK;
    }

    return validators;
  }

  @HostListener('document:pointerdown', ['$event'])
  onDocumentPointerDown(event: PointerEvent) {
    const box = this.boxRef?.nativeElement;
    if (!box) return;

    const clickedInside = box.contains(event.target as Node);

    if (!clickedInside) {
      this.dismiss();
    }
  }

  ngOnInit(): void {
    this.form.patchValue({
      editableField: this.value(),
    });

    this.form.controls.editableField.setValidators(
      this.getValidatorsByType(this.type()),
    );
  }

  confirmField() {
    this.cellEdited.next({
      value: this.form.value.editableField,
    });
  }

  public dismiss() {
    this.cellEdited.next(null);
  }
}
